/**
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2025, Zumper
 */

import { PropertyFeatures } from '@zcode/enums'
import { Bizo, autoclass } from './base'
import type {
  LifestyleScore as LifestyleScoreType,
  RawLifestyleScore,
} from './lifestyleScore.types'

export const TITLES = {
  [PropertyFeatures.GOOD_FOR_PETS]: 'Pet owners',
  [PropertyFeatures.GOOD_FOR_SOCIAL_LIFE]: 'Social life',
  [PropertyFeatures.GOOD_FOR_ECO]: 'Eco living',
  [PropertyFeatures.GOOD_FOR_WFH]: 'Work-from-home',
  [PropertyFeatures.GOOD_FOR_WELLNESS]: 'Wellness',
} as const

export const LifestyleScore: typeof LifestyleScoreType = autoclass<
  LifestyleScoreType,
  RawLifestyleScore
>(
  'LifestyleScore',
  class LifestyleScore extends Bizo<LifestyleScoreType, RawLifestyleScore> {
    static fromJS(data: undefined): undefined
    static fromJS(data: null): null
    static fromJS(data: RawLifestyleScore): LifestyleScoreType
    static fromJS(data: readonly RawLifestyleScore[]): readonly LifestyleScoreType[]
    static fromJS(
      data: readonly RawLifestyleScore[] | RawLifestyleScore | null | undefined
    ): readonly LifestyleScoreType[] | LifestyleScoreType | null | undefined
    static fromJS(
      data: readonly RawLifestyleScore[] | RawLifestyleScore | null | undefined
    ): readonly LifestyleScoreType[] | LifestyleScoreType | null | undefined {
      if (Array.isArray(data)) {
        return data.map((item) => new LifestyleScore(item) as LifestyleScoreType)
      }
      return super.fromJS<RawLifestyleScore, typeof LifestyleScoreType>(data)
    }
  } as typeof Bizo<LifestyleScoreType, RawLifestyleScore>,
  {
    content: 'lifestyle_score_entries',
    feature: 'lifestyle_score_enum',
    title: ({ feature }: LifestyleScoreType) => TITLES[feature],
  }
)

export type LifestyleScore = LifestyleScoreType
